/**
 * Enrforce 24-Hour Giveaway hero: countdown logic.
 * Custom element <enf24-hero>, no dependencies. Safe to load more than once.
 *
 * Reads from the element's data attributes (set by sections/enf24-hero.liquid):
 *   data-start, data-end        ISO 8601 with a UTC offset, e.g. 2026-10-11T00:00:00-06:00
 *   data-before-target          "start" | "end"  (what the clock counts to before the window opens)
 *   data-label-before / -live / -ended     small label, top left of the clock panel
 *   data-badge-before / -live              small label, top right of the clock panel ("CLOSED" after the end)
 *   data-tick-before                       label under the 24-tick bar before the window ("ENDED" after the end)
 *
 * Phases (also written to data-phase on the element, for CSS hooks):
 *   before  now <  start
 *   live    start <= now < end
 *   ended   now >= end
 *
 * Display: DD:HH:MM:SS while 24 hours or more remain, HH:MM:SS under 24 hours.
 */
(function () {
  if (window.customElements.get('enf24-hero')) return;

  var HOUR = 3600000;

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  class Enf24Hero extends HTMLElement {
    connectedCallback() {
      this.start = Date.parse(this.dataset.start);
      this.end = Date.parse(this.dataset.end);
      this.digits = this.querySelector('[data-enf24-digits]');
      this.label = this.querySelector('[data-enf24-label]');
      this.badge = this.querySelector('[data-enf24-badge]');
      this.tickLabel = this.querySelector('[data-enf24-tick-label]');
      this.ticks = Array.prototype.slice.call(this.querySelectorAll('[data-enf24-tick]'));
      this.unitEls = [];
      this.unitCount = 0;

      if (isNaN(this.start) || isNaN(this.end)) {
        // Bad or missing dates: leave the server-rendered placeholders and say why.
        console.warn('[enf24-hero] Start/end time is missing or not ISO 8601 with an offset.', this.dataset.start, this.dataset.end);
        return;
      }

      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = window.setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    /** Offset for QA: add ?enf24_now=2026-10-11T12:00:00-06:00 to the URL to preview any moment. */
    now() {
      if (this._offset === undefined) {
        var forced = new URLSearchParams(window.location.search).get('enf24_now');
        var parsed = forced ? Date.parse(forced) : NaN;
        this._offset = isNaN(parsed) ? 0 : parsed - Date.now();
      }
      return Date.now() + this._offset;
    }

    tick() {
      var now = this.now();
      var phase = now < this.start ? 'before' : now < this.end ? 'live' : 'ended';
      var target = phase === 'before' && this.dataset.beforeTarget !== 'end' ? this.start : this.end;
      var ms = Math.max(0, target - now);
      var total = Math.floor(ms / 1000);

      var days = Math.floor(total / 86400);
      var units;
      if (days >= 1) {
        units = [
          [pad(days), 'DAYS'],
          [pad(Math.floor((total % 86400) / 3600)), 'HRS'],
          [pad(Math.floor((total % 3600) / 60)), 'MIN'],
          [pad(total % 60), 'SEC']
        ];
      } else {
        units = [
          [pad(Math.floor(total / 3600)), 'HOURS'],
          [pad(Math.floor((total % 3600) / 60)), 'MINUTES'],
          [pad(total % 60), 'SECONDS']
        ];
      }

      this.renderUnits(units);
      var text = units.map(function (u) { return u[0]; }).join(':');

      // 24-tick bar: one tick per hour left in the live window.
      var hoursLeft = phase === 'live' ? Math.min(24, Math.ceil((this.end - now) / HOUR)) : 0;
      this.ticks.forEach(function (el, i) { el.classList.toggle('is-lit', i < hoursLeft); });

      if (phase !== this.phase) {
        this.phase = phase;
        this.dataset.phase = phase;
        if (this.label) this.label.textContent = this.dataset['label' + cap(phase)] || '';
        if (this.badge) this.badge.textContent = phase === 'ended' ? 'CLOSED' : this.dataset['badge' + cap(phase)] || '';
      }
      if (this.tickLabel) {
        this.tickLabel.textContent = phase === 'live'
          ? hoursLeft + ' OF 24 HRS LEFT'
          : phase === 'before' ? this.dataset.tickBefore || '' : 'ENDED';
      }
      if (phase === 'ended') window.clearInterval(this.timer);
    }

    renderUnits(units) {
      if (!this.digits) return;
      if (units.length !== this.unitCount) {
        // Rebuild only when the layout switches between 4 and 3 units.
        this.unitCount = units.length;
        this.digits.dataset.units = String(units.length);
        this.digits.textContent = '';
        this.unitEls = units.map(function (u, i) {
          var unit = document.createElement('div');
          unit.className = 'enf24__unit';
          var num = document.createElement('div');
          num.className = 'enf24__unit-num';
          var ghost = document.createElement('span');
          ghost.className = 'enf24__unit-ghost';
          ghost.setAttribute('aria-hidden', 'true');
          ghost.textContent = '88';
          var val = document.createElement('span');
          val.className = 'enf24__unit-val';
          var label = document.createElement('div');
          label.className = 'enf24__unit-label';
          num.appendChild(ghost);
          num.appendChild(val);
          unit.appendChild(num);
          unit.appendChild(label);
          this.digits.appendChild(unit);
          if (i < units.length - 1) {
            var colon = document.createElement('div');
            colon.className = 'enf24__colon';
            colon.setAttribute('aria-hidden', 'true');
            colon.textContent = ':';
            this.digits.appendChild(colon);
          }
          return { val: val, label: label };
        }, this);
      }
      units.forEach(function (u, i) {
        var els = this.unitEls[i];
        if (els.val.textContent !== u[0]) els.val.textContent = u[0];
        if (els.label.textContent !== u[1]) els.label.textContent = u[1];
      }, this);
    }
  }

  function cap(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  window.customElements.define('enf24-hero', Enf24Hero);
})();
